/**
 * Admin — KCIS Adaptive Reporting (Sprint C)
 *
 * Mounts at /admin/kcis-adaptive-reporting. Super-admin only.
 *
 * Surfaces:
 *   - GET  /api/admin/kcis/reporting/summary
 *   - GET  /api/admin/kcis/reporting/conversion
 *   - GET  /api/admin/kcis/airtable/status
 *
 * Intentionally minimal — operational visibility over visual polish for v1.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, adminAuth } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function StatBlock({ title, items, testid }) {
  const entries = Object.entries(items || {}).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <h3 className="text-xs uppercase tracking-widest text-purple-900 font-semibold mb-3">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500">No data yet.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map(([k, v]) => (
            <li key={k} className="flex justify-between text-sm">
              <span className="text-slate-700">{k}</span>
              <span className="font-semibold tabular-nums">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function AdminKcisAdaptiveReporting() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [airtable, setAirtable] = useState(null);
  const [since, setSince] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!adminAuth.isSuperAdmin?.()) { navigate("/admin"); return; }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const qs = since ? `?since_days=${since}` : "";
        const [s, c, a] = await Promise.all([
          api.get(`/admin/kcis/reporting/summary${qs}`),
          api.get(`/admin/kcis/reporting/conversion${qs}`),
          api.get(`/admin/kcis/airtable/status`),
        ]);
        if (cancelled) return;
        setSummary(s.data); setConversion(c.data); setAirtable(a.data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e, "Reporting load failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [since]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <TopNav />
      <main className="flex-1 max-w-7xl mx-auto px-6 py-10 w-full" data-testid="admin-kcis-reporting-page">
        <header className="mb-6 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-purple-900 font-semibold">KCIS Adaptive — Sprint C</p>
            <h1 className="text-2xl sm:text-3xl font-semibold mt-1">Reporting</h1>
            <p className="text-sm text-slate-600 mt-1">Door + readiness + archetype rollups, conversion funnel, Airtable mirror health.</p>
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-sm flex items-center gap-2">
              <span className="text-xs text-slate-500">Window</span>
              <select className="border rounded-md px-3 py-2 text-sm" value={since} onChange={(e) => setSince(e.target.value)} data-testid="admin-kcis-reporting-window">
                <option value="">All time</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </label>
            <Button variant="outline" onClick={() => navigate("/admin/kcis-adaptive-sessions")} data-testid="admin-kcis-reporting-sessions-link">View sessions →</Button>
            <Button variant="outline" onClick={() => navigate("/admin/kcis-make-operations")} data-testid="admin-kcis-reporting-make-link">Make automation →</Button>
          </div>
        </header>

        {error && <p className="text-rose-700 text-sm mb-3" data-testid="admin-kcis-reporting-error">{error}</p>}
        {loading && <p className="text-sm text-slate-500" data-testid="admin-kcis-reporting-loading">Loading…</p>}

        {summary && (
          <section className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="admin-kcis-reporting-totals">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Sessions</p>
                <p className="text-2xl font-semibold mt-1">{summary.total_sessions}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Completed</p>
                <p className="text-2xl font-semibold mt-1">{summary.completed_sessions}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Completion rate</p>
                <p className="text-2xl font-semibold mt-1">{((summary.completion_rate || 0) * 100).toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Airtable mirror</p>
                <p className="text-sm font-semibold mt-2">
                  <Badge variant={airtable?.enabled ? "default" : "outline"} data-testid="admin-kcis-reporting-airtable-state">
                    {airtable?.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </p>
                <p className="text-[11px] text-slate-500 mt-1">{airtable?.totals?.ok || 0} ok / {airtable?.totals?.failed || 0} failed</p>
              </div>
            </div>
          </section>
        )}

        {conversion && (
          <section className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="admin-kcis-reporting-funnel">
            <h3 className="text-xs uppercase tracking-widest text-purple-900 font-semibold mb-4">Stage funnel</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-center">
              {Object.entries(conversion.stages || {}).map(([stage, count]) => (
                <div key={stage} className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">{stage}</p>
                  <p className="text-xl font-semibold mt-1">{count}</p>
                </div>
              ))}
              <div className="rounded-lg border border-purple-200 p-3 bg-purple-50">
                <p className="text-[10px] uppercase tracking-widest text-purple-900">CTA clicked</p>
                <p className="text-xl font-semibold mt-1">{conversion.cta_clicked || 0}</p>
              </div>
              <div className="rounded-lg border border-purple-200 p-3 bg-purple-50">
                <p className="text-[10px] uppercase tracking-widest text-purple-900">Funnel confirmed</p>
                <p className="text-xl font-semibold mt-1">{conversion.funnel_confirmed || 0}</p>
              </div>
              <div className="rounded-lg border border-amber-200 p-3 bg-amber-50">
                <p className="text-[10px] uppercase tracking-widest text-amber-900">Legacy form used</p>
                <p className="text-xl font-semibold mt-1">{conversion.legacy_form_used || 0}</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Duplicate-capture avoided: <strong>{((conversion.duplicate_capture_avoided_rate || 0) * 100).toFixed(1)}%</strong>
            </p>
          </section>
        )}

        {summary && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <StatBlock title="By assigned door" items={summary.by_door} testid="admin-kcis-reporting-by-door" />
            <StatBlock title="By readiness band" items={summary.by_readiness_band} testid="admin-kcis-reporting-by-readiness" />
            <StatBlock title="By primary archetype" items={summary.by_archetype} testid="admin-kcis-reporting-by-archetype" />
            <StatBlock title="By source domain" items={summary.by_source} testid="admin-kcis-reporting-by-source" />
            <StatBlock title="By UTM source" items={summary.by_utm_source} testid="admin-kcis-reporting-by-utm-source" />
            <StatBlock title="By UTM campaign" items={summary.by_utm_campaign} testid="admin-kcis-reporting-by-utm-campaign" />
            <StatBlock title="By engine version" items={summary.by_engine_version} testid="admin-kcis-reporting-by-engine" />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
